import type { AllocationDetailsDto, TradeAllocationFormResult } from 'models/forms/TradeAllocationFormResult';
import type { Action } from 'state/actions';
import { addKey, patchKey } from 'state/utils/collection';

import type {
  ApplyStatus,
  TradeAllocationForm,
  TradeAllocationFormLoadingStatus,
  TradeAllocationFormState,
  TradeAllocationFormTabState,
  TradeAllocationRowModel,
} from './tradeAllocationForm.model';

function mapForm(result: TradeAllocationFormResult): TradeAllocationForm {
  return {
    ...result,
    allocationDetails: mapAllocationDetails(result.allocationDetails),
  };
}

function mapAllocationDetails(allocationDetails?: AllocationDetailsDto[]): TradeAllocationRowModel[] | undefined {
  return allocationDetails?.map(x => ({
    ...x,
    independentAmount: x.independentAmount ?? undefined,
    isManuallyAdded: false,
    isExpanded: x.independentAmount !== null,
  }));
}

export function tradeAllocationFormReducer(
  state: TradeAllocationFormState = {},
  action: Action,
): TradeAllocationFormState {
  switch (action.type) {
    case 'TRADE_ALLOCATION_FORM_REQUESTED':
      if (state[action.tabId]) {
        return state;
      }

      const tabState: Partial<TradeAllocationFormTabState> = {
        info: {
          tradeId: action.tradeId,
          product: action.product,
          isin: action.isin,
          wrapper: action.wrapper,
          fileCode: action.fileCode,
          productId: action.productId,
          rfqId: action.rfqId,
        },
        loadingStatus: 'Pending',
        applyStatus: 'Initialized',
      };
      return addKey(state, action.tabId, tabState);
    case 'TRADE_ALLOCATION_FORM_RECEIVED':
      return patchKey(state, action.tabId, () => {
        const tabState: Partial<TradeAllocationFormTabState> = {
          form: mapForm(action.tradeAllocationFormResult),
          initialAllocations: mapAllocationDetails(action.tradeAllocationFormResult.allocationDetails),
          loadingStatus: 'Success',
        };

        return tabState;
      });
    case 'TRADE_ALLOCATION_FORM_RESET_RECEIVED':
      return patchKey(state, action.tabId, () => {
        const tabState: Partial<TradeAllocationFormTabState> = {
          form: mapForm(action.tradeAllocationFormResult),
          initialAllocations: mapAllocationDetails(action.tradeAllocationFormResult.allocationDetails),
          loadingStatus: 'Success',
          applyStatus: 'Initialized',
          applyError: undefined,
          applyResponse: undefined,
          fightingBackJustification: undefined,
          fightingBackComment: undefined,
        };

        return tabState;
      });
    case 'TRADE_ALLOCATION_FORM_REQUEST_FAILED':
      return patchKey(state, action.tabId, () => {
        const tabState: Partial<TradeAllocationFormTabState> = {
          loadingStatus: 'Error',
          loadingError: action.error,
        };
        return tabState;
      });
    case 'TRADE_ALLOCATION_CUSTODIANS_RELOAD_FAILED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        const custodians = { isSuccess: false, error: action.error };
        return {
          form: {
            ...currentFormResult,
            custodians,
            allocationDetails: [],
          },
          initialAllocations: [],
        };
      });
    case 'TRADE_ALLOCATION_FORM_REQUEST_TIMED_OUT':
      return patchKey(state, action.tabId, () => {
        const tabState: Partial<TradeAllocationFormTabState> = {
          loadingStatus: 'Error',
          loadingError: 'TIMEOUT',
        };
        return tabState;
      });
    case 'TRADE_ALLOCATION_FIGHTING_BACK_JUSTIFICATION_CHANGED':
      return patchKey(state, action.tabId, () => {
        return {
          fightingBackJustification: action.justification,
        };
      });
    case 'TRADE_ALLOCATION_FIGHTING_BACK_COMMENT_CHANGED':
      return patchKey(state, action.tabId, () => {
        return {
          fightingBackComment: action.comment,
        };
      });
    case 'TRADE_ALLOCATION_CLIENT_CHANGED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        const cleanedAllocations = currentFormResult?.allocationDetails?.map(allocation => ({
          ...allocation,
          custodianCode: undefined,
        }));

        const newClient =
          action.client === null
            ? undefined
            : {
                bdrId: action.client?.id,
                name: action.client?.label,
              };

        return {
          form: {
            ...currentFormResult,
            client: newClient,
            allocationDetails: cleanedAllocations,
          },
        };
      });
    case 'TRADE_ALLOCATION_CUSTODIANS_RELOADED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        const custodians = { isSuccess: true, data: action.custodians };
        return {
          form: {
            ...currentFormResult,
            custodians,
          },
        };
      });
    case 'TRADE_ALLOCATION_ROW_CHANGED':
      return patchKey(state, action.tabId, ({ form }) => {
        const start = form?.allocationDetails?.slice(0, action.index) ?? [];
        const end = form?.allocationDetails?.slice(action.index + 1) ?? [];
        const allocationDetails = [...start, action.allocation, ...end];
        return {
          form: {
            ...form,
            allocationDetails,
          },
        };
      });
    case 'TRADE_ALLOCATION_ROW_REMOVED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        const start = currentFormResult?.allocationDetails?.slice(0, action.index) ?? [];
        const end = currentFormResult?.allocationDetails?.slice(action.index + 1) ?? [];
        const allocationDetails = [...start, ...end];
        return {
          form: {
            ...currentFormResult,
            allocationDetails,
          },
        };
      });
    case 'TRADE_ALLOCATION_APPLY_REQUESTED':
      return patchKey(state, action.tabId, () => {
        return {
          applyStatus: 'Pending' as ApplyStatus,
          applyStartingTimeMs: action.startTimeMs,
          applyEstimatedDurationTimeMs: action.estimatedDurationTimeMs,
        };
      });
    case 'TRADE_ALLOCATION_APPLY_RESPONSE_RECEIVED':
      return patchKey(state, action.tabId, () => {
        return {
          applyResponse: action.response,
          applyStatus: 'Done' as ApplyStatus,
          applyStartingTimeMs: undefined,
          applyEstimatedDurationTimeMs: undefined,
        };
      });
    case 'TRADE_ALLOCATION_APPLY_REQUEST_FAILED':
      return patchKey(state, action.tabId, () => {
        return {
          applyError: action.technicalError,
          applyStatus: 'Error' as ApplyStatus,
          applyStartingTimeMs: undefined,
          applyEstimatedDurationTimeMs: undefined,
        };
      });
    case 'TRADE_ALLOCATION_NOTIONAL_CHANGED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        return {
          form: {
            ...currentFormResult,
            notional: action.newNotional,
          },
        };
      });
    case 'TRADE_ALLOCATION_TRADING_PRICE_CHANGED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        return {
          form: {
            ...currentFormResult,
            tradingPrice: action.newTradingPrice,
          },
        };
      });
    case 'TRADE_ALLOCATION_OFFER_PRICE_CHANGED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        return {
          form: {
            ...currentFormResult,
            offerPrice: action.newOfferPrice,
          },
        };
      });
    case 'TRADE_ALLOCATION_SPONSO_CHANGED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        return {
          form: {
            ...currentFormResult,
            sponso: action.newSponso,
          },
        };
      });
    case 'TRADE_ALLOCATION_SPONSO_PERCENT_UPFRONT_CHANGED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        return {
          form: {
            ...currentFormResult,
            sponsoPercentUpFront: action.newSponsoPercentUpFront,
          },
        };
      });
    case 'TRADE_ALLOCATION_F2Y_CHANGED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        return {
          form: {
            ...currentFormResult,
            f2Y: action.newF2Y,
          },
        };
      });
    case 'TRADE_ALLOCATION_TOP_UP_PERCENT_UPFRONT_CHANGED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        return {
          form: {
            ...currentFormResult,
            topUpPercentUpFront: action.newTopUpPercentUpfront,
          },
        };
      });
    case 'TRADE_ALLOCATION_UPFRONT_CHANGED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        return {
          form: {
            ...currentFormResult,
            upfrontFee: action.newUpfront,
          },
        };
      });
    case 'TRADE_ALLOCATION_XMARGIN_CHANGED':
      return patchKey(state, action.tabId, ({ form: currentFormResult }) => {
        return {
          form: {
            ...currentFormResult,
            salesMargin: action.newXMargin,
          },
        };
      });
    case 'TRADE_ALLOCATION_FORM_RESET_REQUESTED':
      return patchKey(state, action.tabId, () => {
        return {
          loadingStatus: 'Resetting' as TradeAllocationFormLoadingStatus,
          loadingError: undefined,
          applyError: undefined,
          applyStatus: 'Initialized' as ApplyStatus,
        };
      });
  }
  return state;
}
